import { observer } from 'mobx-react-lite'
import { Icon } from 'shared/ui/Icon'
import { Typography } from 'shared/ui/Typography'
import { Tooltip } from 'shared/ui/Tooltip'
import { DaysDropdown } from './DaysDropdown'
import { SettingsTabs, type ITabsListItem } from './SettingsTabs'
import styles from './styles.module.scss'

export type IFollowUpAssertiveness = 'subtle' | 'steady' | 'assertive'

type IAutoFollowUpProps = {
  followUpWindow: number
  followUpAssertiveness: IFollowUpAssertiveness
  setFollowUpWindow: (value: number) => void
  setFollowUpAssertiveness: (value: IFollowUpAssertiveness) => void
  tabsClassName?: string
  assertivenessLabel?: string
}

const ASSERTIVENESS_TABS: ITabsListItem<IFollowUpAssertiveness>[] = [
  { value: 'subtle', label: 'Subtle', icon: 'chart1' },
  { value: 'steady', label: 'Steady', icon: 'chart2' },
  { value: 'assertive', label: 'Assertive', icon: 'chart3' },
]

export const AutoFollowUp = observer(
  ({
    followUpWindow,
    followUpAssertiveness,
    setFollowUpWindow,
    setFollowUpAssertiveness,
    tabsClassName,
    assertivenessLabel,
  }: IAutoFollowUpProps) => {
    return (
      <>
        <DaysDropdown
          label='Follow-up window'
          value={followUpWindow}
          onChange={setFollowUpWindow}
          days={[
            { value: 3, label: '3' },
            { value: 7, label: '7' },
            { value: 14, label: '14' },
          ]}
        />
        <Typography
          variant='text-input-field-r'
          color='var(--content-primary-secondary)'
          ariaLabel='followUpAssertivenessLabel'
          className={styles.followUpAssertivenessLabel}
          marginBottom={12}
          tag='div'
        >
          {assertivenessLabel || 'How often should the agent follow up?'}
          <Tooltip
            label='The agent follows up within the set window.'
            desc={
              <>
                <span className={styles.tooltipValue}>• Subtle:</span> Few reminders
                <br />
                <span className={styles.tooltipValue}>• Steady:</span> Balanced reminders
                <br />
                <span className={styles.tooltipValue}>• Assertive:</span> Frequent reminders
              </>
            }
            type='description'
            placement='top'
            breakWord
            maxWidth={360}
          >
            <Icon icon='question' fontSize={14} color='var(--content-neutral-primary)' />
          </Tooltip>
        </Typography>
        <SettingsTabs
          tabsList={ASSERTIVENESS_TABS}
          selectedValue={followUpAssertiveness}
          setSelectedValue={setFollowUpAssertiveness}
          tabsClassName={tabsClassName}
        />
      </>
    )
  }
)
